import Image, { ImageProps } from "next/image";
import { rgba } from "polished";
import { useCallback, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import useClickOutside from "../hooks/useClickOutside";
import { useEscKey } from "../hooks/useEscKey";
import useLockDocumentScroll from "../hooks/useLockDocumentScroll";
import { useWindowDimensions } from "../hooks/useWindowDimensions";
import { pxToRem } from "@input-output-hk/px-to-rem";
import colors from "../tokens/colors";
import zIndex from "../tokens/z-index";
import { CloseImageButton } from "./icons/CloseImageButton";
import { ExpandImageButton } from "./icons/ExpandImageButton";

// as per https://input-output.atlassian.net/browse/WEB-1049
const maxWidthForMaximisedImage = 1400;

const StyledEnhancedViewer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100vw;
  width: 100%;
  align-items: flex-start;
  justify-content: center;
  position: relative;
`;

const styledOpenAndCloseButtons = css`
  opacity: 0;
  transition: all 0.75s;
  position: absolute;
  bottom: ${pxToRem(20)};
  right: ${pxToRem(10)};
  cursor: pointer;

  &:hover {
    opacity: 1;
    transition: all 0.75s;
    cursor: pointer;
  }
`;

const OpenMaximisedImageButton = styled(ExpandImageButton)`
  ${styledOpenAndCloseButtons}

  &:hover {
    opacity: 1;
    transition: all 0.75s;
    cursor: pointer;
  }
`;

const CloseMaximisedImageButton = styled(CloseImageButton)`
  ${styledOpenAndCloseButtons}

  position: fixed;
  top: ${pxToRem(20)};
  right: ${pxToRem(20)};
  transition: all 0.5s;
  z-index: ${zIndex.dialog + 1};

  opacity: 1;
`;

const MaximisedImageButtonAndOverlayWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: ${zIndex.dialog};
  position: fixed;
  left: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 0 ${pxToRem(20)};
  height: 100vh;
`;

const MaximisedImageAndButtonWrapper = styled.div`
  position: relative;
  z-index: ${zIndex.dialog + 1};
  max-height: 100vh;
`;

const MinimisedImageAndButtonWrapper = styled.div`
  position: relative;

  &:hover ${OpenMaximisedImageButton} {
    opacity: 1;
  }
`;

const MinimisedImage = styled(Image)`
  width: 100%;
  cursor: pointer;
  border-radius: ${pxToRem(15)};
`;

const MaximisedImage = styled(Image)`
  width: 100%;
  max-width: ${pxToRem(maxWidthForMaximisedImage)};
`;

const Overlay = styled.div`
  background: ${rgba(colors.secondary.midnightBlue, 0.7)};
  height: 100vh;
  width: 100vw;
  position: fixed;
  left: 0;
  top: 0;
`;

const OutsideClickWrapper = styled.div`
  margin: 0;
  padding: 0;
  position: relative;
  top: ${pxToRem(5)};
`;

type Props = ImageProps & {
  className?: string;
};

export function EnhancedViewer({
  className,
  src,
  alt,
  width,
  height,
  blurDataURL
}: Props) {
  const [showMaximisedImage, setShowMaximisedImage] = useState(false);
  const [maximisedImageWidth, setMaximisedImageWidth] = useState<number>(0);
  const [maximisedImageHeight, setMaximisedImageHeight] = useState<number>(0);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const { viewportHeight } = useWindowDimensions();

  const ref = useRef<HTMLDivElement>(null);

  useEscKey(closeMaximisedImage);
  useLockDocumentScroll(showMaximisedImage, "html");

  const closeIfOpen = useCallback(() => {
    if (showMaximisedImage) {
      closeMaximisedImage();
    }
  }, [showMaximisedImage]);

  useClickOutside(ref, closeIfOpen);

  useEffect(() => {
    if (!viewportHeight) return;

    const img: HTMLImageElement = new (window as any).Image();
    img.src = src as HTMLImageElement["src"];

    img.onload = () => {
      let height: HTMLImageElement["height"];

      if (img.width >= maxWidthForMaximisedImage) {
        const ratio = maxWidthForMaximisedImage / img.width;
        height = Math.round(ratio * img.height);
        setMaximisedImageWidth(maxWidthForMaximisedImage);
      } else {
        setMaximisedImageWidth(img.width);
        height = Math.round(img.height);
      }

      setMaximisedImageHeight(height);

      if (viewportHeight > height) {
        setShowScrollbar(false);
      } else {
        setShowScrollbar(true);
      }
    };
  }, [viewportHeight, src]);

  function openMaximisedImage() {
    setShowMaximisedImage(true);
  }

  function closeMaximisedImage() {
    setShowMaximisedImage(false);
  }

  return (
    <StyledEnhancedViewer className={className}>
      <MinimisedImageAndButtonWrapper>
        <MinimisedImage
          src={src}
          alt={alt}
          onClick={openMaximisedImage}
          width={width}
          height={height}
          blurDataURL={blurDataURL}
          priority={true}
        />

        {!showMaximisedImage && (
          <OpenMaximisedImageButton onClick={openMaximisedImage} />
        )}
      </MinimisedImageAndButtonWrapper>

      {showMaximisedImage && (
        <MaximisedImageButtonAndOverlayWrapper role="presentation">
          <MaximisedImageAndButtonWrapper
            style={{
              overflow: showScrollbar ? "auto" : "initial"
            }}
          >
            <CloseMaximisedImageButton onClick={closeMaximisedImage} />

            <OutsideClickWrapper ref={ref}>
              <MaximisedImage
                src={src}
                alt={alt}
                width={maximisedImageWidth}
                height={maximisedImageHeight}
                blurDataURL={blurDataURL}
              />
            </OutsideClickWrapper>
          </MaximisedImageAndButtonWrapper>

          <Overlay />
        </MaximisedImageButtonAndOverlayWrapper>
      )}
    </StyledEnhancedViewer>
  );
}
